import { useMemo } from 'react';
import useSWR from 'swr';
import { IconLayoutDashboard } from '@tabler/icons-react';

import { getCurrentUser } from 'services/meService';
import { transformMenuDtoToNavItems, filterActionsFromTree } from 'utils/menuTransformer';
import { normalizeMenuTree } from 'utils/normalizeMenuFromApi';
import { NavItemType } from 'types';
import { MenuDto } from 'types/menu';
import useAuth from 'hooks/useAuth';
import { resolveUserRole } from 'utils/resolveUserRole';
import { FORM_ROLE } from 'utils/createFormRolePolicy';

// ==============================|| HOOK - DYNAMIC MENU ||============================== //

interface UseDynamicMenuReturn {
  /** Filtered NavItemType[] for the sidebar (action leaves removed). */
  menuItems: NavItemType[];
  /** Raw MenuDto tree from the API, unfiltered — use this in workspace components to read available actions. */
  rawMenus: MenuDto[];
  /** Permission keys returned by the API — used as fallback when menus are flat. */
  permissions: string[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Fetches the menu from GET /Users/GetCurrentUser and transforms it
 * into Berry's NavItemType[] format.
 *
 * - Only fetches when the user is authenticated.
 * - Action-level leaf items (create_report, view_pending, etc.) are stripped
 *   from the sidebar tree; their parent modules become clickable workspace links.
 * - rawMenus exposes the unfiltered tree so workspace pages can read actions.
 */
export function useDynamicMenu(): UseDynamicMenuReturn {
  const { isLoggedIn, user } = useAuth();
  const isAdmin = resolveUserRole(user) === FORM_ROLE.ADMIN;

  const { data, isLoading, error } = useSWR(
    isLoggedIn ? 'me/current-user' : null,
    getCurrentUser,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      shouldRetryOnError: true,
      errorRetryCount: 2
    }
  );

  const rawMenus = useMemo<MenuDto[]>(() => {
    if (!data || typeof data !== 'object') return [];
    const d = data as unknown as Record<string, unknown>;
    const m = d.menus ?? d.Menus;
    return normalizeMenuTree(m);
  }, [data, error]);
  const permissions = useMemo<string[]>(() => {
    if (!data || typeof data !== 'object') return [];
    const d = data as unknown as Record<string, unknown>;
    const p = d.permissions ?? d.Permissions;
    return Array.isArray(p) ? (p as string[]) : [];
  }, [data]);

  // Action items (depth ≥ 2 leaves) are stripped from the sidebar tree.
  // They remain in rawMenus and are accessible via useWorkspaceMenu + ?view URL params.
  const apiMenuItems = useMemo<NavItemType[]>(
    () => (rawMenus.length > 0 ? transformMenuDtoToNavItems(filterActionsFromTree(rawMenus)) : []),
    [rawMenus]
  );

  // Dashboard is Admin-only for now (per product decision) — Doctor, Technician, and Manager
  // have no dashboard view and land on their first available menu item instead.
  const dashboardGroup = useMemo<NavItemType>(
    () => ({
      id: 'nav-dashboard-group',
      title: 'Main',
      type: 'group',
      children: [
        {
          id: 'nav-dashboard',
          title: 'Dashboard',
          type: 'item',
          url: '/dashboard/default',
          link: '/dashboard/default',
          icon: IconLayoutDashboard,
          breadcrumbs: false
        }
      ]
    }),
    []
  );

  const menuItems = useMemo<NavItemType[]>(
    () => (isAdmin ? [dashboardGroup, ...apiMenuItems] : apiMenuItems),
    [dashboardGroup, apiMenuItems, isAdmin]
  );

  return { menuItems, rawMenus, permissions, isLoading, isError: !!error };
}
